/**
 * Tabela genérica: sem zebra, filete de 1px entre linhas, colunas numéricas
 * à direita em Plex Mono (ver `.table` em base.css). `onRowClick` é só uma
 * conveniência visual — para acessibilidade, ponha um link/botão real numa
 * das colunas (ex.: o código da proposta) além do clique na linha.
 */
import type { ReactNode } from 'react';
import { Spinner } from './Spinner';

export interface DataTableColumn<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  /** Alinha à direita e usa Plex Mono tabular — para dinheiro, alíquota, quantidade, data. */
  numeric?: boolean;
  className?: string;
  headerClassName?: string;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  /** Conteúdo mostrado quando `rows` está vazio (normalmente um `<EmptyState>`). */
  empty?: ReactNode;
  loading?: boolean;
  caption?: string;
  className?: string;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
  loading,
  caption,
  className,
}: DataTableProps<T>) {
  return (
    <div className="table-wrap">
      <table className={['table', className].filter(Boolean).join(' ')}>
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={[col.numeric ? 'num' : '', col.headerClassName].filter(Boolean).join(' ')}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr className="table-empty">
              <td colSpan={columns.length}>
                <Spinner label="Carregando…" />
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr className="table-empty">
              <td colSpan={columns.length}>{empty}</td>
            </tr>
          ) : (
            rows.map((row) => {
              const key = rowKey(row);
              return (
                <tr
                  key={key}
                  className={onRowClick ? 'is-link' : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (event.key === 'Enter') onRowClick(row);
                        }
                      : undefined
                  }
                >
                  {columns.map((col) => (
                    <td key={col.key} className={[col.numeric ? 'num' : '', col.className].filter(Boolean).join(' ')}>
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
